import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const navigate = useNavigate();

  const fetchUserRole = async (userId: string) => {
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    const userRoles = roles?.map(r => r.role) || [];
    setIsAdmin(userRoles.includes('admin') || userRoles.includes('super_admin' as any));
    setIsSuperAdmin(userRoles.includes('super_admin' as any));
    
    // SECURITY: Check for server-validated impersonation session
    const impersonationToken = sessionStorage.getItem('impersonation_token');
    if (impersonationToken) {
      const { data: isValid } = await supabase.rpc('validate_impersonation_token', {
        _token: impersonationToken,
        _admin_id: userId
      });
      setIsImpersonating(!!isValid);
      if (!isValid) {
        // Clear invalid token
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonating');
      }
    } else {
      setIsImpersonating(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          setTimeout(() => {
            fetchUserRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        fetchUserRole(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('currentOrganizationId');
    sessionStorage.removeItem('impersonating');
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('original_admin_session');
    navigate("/auth");
  };

  const exitImpersonation = () => {
    sessionStorage.removeItem('impersonating');
    sessionStorage.removeItem('impersonation_token');
    setIsImpersonating(false);
    window.location.reload();
  };

  return { 
    user, 
    session, 
    loading, 
    isAdmin, 
    isSuperAdmin,
    isImpersonating,
    exitImpersonation,
    signOut 
  };
};
